import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, CardHeader, CardBody, CardFooter, Label } from 'reactstrap';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import BusinessCard from 'views/HR/DigitalBusinessCards/BusinessCard';
import { callApi, showSweetAlert } from 'utils/utils';
import { config } from 'utils/config';
import PageContent from 'layouts/PageContent';
import FieldText from 'components/Controls/FieldText';
import FieldDropdown from 'components/Controls/FieldDropdown';
import FieldButton from 'components/Controls/FieldButton/FieldButton';
import FieldUpload from 'components/Controls/FieldUpload/FieldUpload';
import FieldPhone from 'components/Controls/FieldPhone/FieldPhone';
import { isValidPhoneNumber } from 'react-phone-number-input';
import defaultAvatar from 'assets/img/default-avatar.png';
import { object, string, array } from 'yup';
import YupValidator from 'utils/YupValidator';
import {
  buildPreviewSrcDoc,
  buildSignatureData,
  findDefaultPhoneRows,
  formatPhoneForSignature,
  isMobilePhoneRow,
  resolveFeaturedRow,
  writeSignatureToClipboard,
} from './signatureUtils';
import './MyBusinessCard.css';

const NONE_OPTION = -1;
const NEW_OPTION = -2;

const MyBusinessCardScreen = () => {
  const { t } = useTranslation(['qr', 'common']);

  const [IsLoading, setIsLoading] = useState(true);
  const [HasCard, setHasCard] = useState(false);
  const [ClaUsuarioBC, setClaUsuarioBC] = useState(null);
  const [FullName, setFullName] = useState('');
  const [JobTitle, setJobTitle] = useState('');
  const [FullAddress, setFullAddress] = useState('');
  const [UserImage, setUserImage] = useState({});
  const [Emails, setEmails] = useState([]);
  const [Phones, setPhones] = useState([]);
  const [PhoneLabelTypes, setPhoneLabelTypes] = useState([]);
  const [EmailLabelTypes, setEmailLabelTypes] = useState([]);

  const [FeaturedEmailId, setFeaturedEmailId] = useState(NONE_OPTION);
  const [MobileContactId, setMobileContactId] = useState(NONE_OPTION);
  const [OfficeContactId, setOfficeContactId] = useState(NONE_OPTION);

  const [NewEmailValue, setNewEmailValue] = useState('');
  const [NewMobileValue, setNewMobileValue] = useState('');
  const [NewOfficeValue, setNewOfficeValue] = useState('');

  const [UserImageFiles, setUserImageFiles] = useState([]);
  const [ShowUploadDialog, setShowUploadDialog] = useState(false);
  const [ShowWalletQRDialog, setShowWalletQRDialog] = useState(false);
  const [WalletQR, setWalletQR] = useState({});
  const [errors, setErrors] = useState({});
  const [BannerVersion, setBannerVersion] = useState(Date.now());
  // Guards the automatic first-visit card creation against retry loops
  const autoCreateTried = useRef(false);

  useEffect(() => {
    getPhoneLabelTypes();
    getEmailLabelTypes();
    getMyBusinessCard(true);
  }, []);

  const getPhoneLabelTypes = () => {
    const url = `${config.UrlApiProject}QRCode/GetBusinessCardLabels?ClaTipoDBCContact=1`;
    callApi(url, 'GET', null, (response) => {
      setPhoneLabelTypes(response.data);
    });
  };

  const getEmailLabelTypes = () => {
    const url = `${config.UrlApiProject}QRCode/GetBusinessCardLabels?ClaTipoDBCContact=2`;
    callApi(url, 'GET', null, (response) => {
      setEmailLabelTypes(response.data);
    });
  };

  // Saved selection semantics live in resolveFeaturedRow (signatureUtils); the slot
  // state holds ContactIds, so map the resolved row back to an id for the dropdowns
  const resolveSlot = (savedId, rows, defaultRow) => {
    const row = resolveFeaturedRow(savedId, rows, defaultRow);
    return row ? row.ContactId : NONE_OPTION;
  };

  const applySelections = (info, emailRows, phoneRows) => {
    const { mobileRow, officeRow } = findDefaultPhoneRows(phoneRows);

    setFeaturedEmailId(resolveSlot(info.FeaturedEmailContactId, emailRows, emailRows.length > 0 ? emailRows[0] : null));
    setMobileContactId(resolveSlot(info.FeaturedMobileContactId, phoneRows, mobileRow));
    setOfficeContactId(resolveSlot(info.FeaturedOfficeContactId, phoneRows, officeRow));
  };

  // Background persist (no spinner, no toast): dropdown changes save immediately so
  // the selection is durable and the admin View Signature shows the same picks
  const persistSelections = (emailId, mobileId, officeId) => {
    const toSaved = (id) => {
      if (typeof id === 'number' && id > 0) {
        return id;
      }
      return id === NONE_OPTION ? 0 : null;
    };

    const url = `${config.UrlApiProject}QRCode/UpdateMySignatureContacts`;
    callApi(url, 'POST', {
      FeaturedEmailContactId: toSaved(emailId),
      FeaturedMobileContactId: toSaved(mobileId),
      FeaturedOfficeContactId: toSaved(officeId),
    }, () => {}, null, false, false);
  };

  const getMyBusinessCard = (applyDefaults) => {
    const url = `${config.UrlApiProject}QRCode/GetMyBusinessCard`;
    callApi(url, 'GET', null, (response) => {
      if (!response.data.HasCard) {
        // First visit: create the card automatically with default values and reload.
        // One attempt only — if the card still doesn't exist afterwards, show the
        // fallback message instead of looping.
        if (!autoCreateTried.current) {
          autoCreateTried.current = true;
          const createUrl = `${config.UrlApiProject}QRCode/CreateMyBusinessCard`;
          callApi(createUrl, 'POST', {}, () => {
            getMyBusinessCard(true);
          }, () => {
            setIsLoading(false);
            setHasCard(false);
          });
          return;
        }

        setIsLoading(false);
        setHasCard(false);
        return;
      }

      setIsLoading(false);
      setHasCard(true);

      setClaUsuarioBC(response.data.info.ClaUsuarioBC);
      setFullName(response.data.info.FullName);
      setJobTitle(response.data.info.JobTitle);
      setFullAddress(response.data.info.FullAddress || '');
      setUserImage({
        file: response.data.info.UserImageFile,
        fileName: response.data.info.UserImageFileName,
        mimeType: response.data.info.UserImageFileMIMEType,
      });
      setEmails(response.data.contactInfo.Email);
      setPhones(response.data.contactInfo.Phone);
      setBannerVersion(Date.now());

      if (applyDefaults) {
        applySelections(response.data.info, response.data.contactInfo.Email, response.data.contactInfo.Phone);
      }
    }, () => {
      setIsLoading(false);
    });
  };

  // Slot state ids -1/-2 (None/Add new) resolve to no row, so only real ContactIds
  // ever reach the image URLs
  const rowById = (rows, id) =>
    typeof id === 'number' && id > 0 ? rows.find((row) => row.ContactId === id) || null : null;

  const signatureData = (version) =>
    buildSignatureData({
      ClaUsuarioBC,
      FullName,
      JobTitle,
      emailRow: rowById(Emails, FeaturedEmailId),
      mobileRow: rowById(Phones, MobileContactId),
      officeRow: rowById(Phones, OfficeContactId),
      version,
    });

  const onCopySignatureClick = async () => {
    // A fresh timestamp per copy guarantees Outlook never reuses cached images
    if (await writeSignatureToClipboard(signatureData(Date.now()))) {
      showSweetAlert(null, t('qr:SignatureCopied'), 'success');
    } else {
      showSweetAlert(null, t('qr:SignatureCopyFailed'), 'error');
    }
  };

  const onShowWalletQRClick = () => {
    const url = `${config.UrlApiProject}QRCode/GetMyWalletPassQRCode`;
    callApi(url, 'GET', null, (response) => {
      setWalletQR({ file: response.data.file, mimeType: response.data.mimeType });
      setShowWalletQRDialog(true);
    });
  };

  const onRemovePhotoClick = () => {
    const url = `${config.UrlApiProject}QRCode/RemoveMyBusinessCardPhoto`;
    callApi(url, 'POST', {}, () => {
      showSweetAlert(null, t('common:Success'), 'success');
      getMyBusinessCard(false);
    });
  };

  const onSaveInfoClick = async () => {
    // Max lengths mirror MSWCatBSCBusinessCard: varchar(100)/varchar(100)/varchar(150)
    const validationScheme = object({
      FullName: string().required(t('common:RequiredField')).max(100, t('qr:MaxLengthExceeded', { max: 100 })).nullable(),
      JobTitle: string().required(t('common:RequiredField')).max(100, t('qr:MaxLengthExceeded', { max: 100 })).nullable(),
      FullAddress: string().max(150, t('qr:MaxLengthExceeded', { max: 150 })).nullable(),
    });

    const validator = new YupValidator(validationScheme);
    const isValid = await validator.validate({ FullName, JobTitle, FullAddress });

    if (!isValid) {
      setErrors(validator.errors);
      return;
    }

    const url = `${config.UrlApiProject}QRCode/UpdateMyBusinessCardInfo`;
    const paramsService = {
      FullName,
      JobTitle,
      FullAddress,
      UserImageFile: UserImage.file,
      UserImageFileName: UserImage.fileName,
    };
    callApi(url, 'POST', paramsService, () => {
      showSweetAlert(null, t('common:Success'), 'success');
      getMyBusinessCard(false);
    });
  };

  const onUserImageFilesChange = (files) => {
    const newErrors = { ...errors };
    delete newErrors.UserImageFiles;
    setErrors(newErrors);
    setUserImageFiles(files);
  };

  const onHideUploadDialog = () => {
    setShowUploadDialog(false);
    setUserImageFiles([]);
  };

  const onSubmitUserImageClick = async () => {
    const validationScheme = object({
      UserImageFiles: array().min(1, t('common:RequiredField')).nullable(),
    });

    const validator = new YupValidator(validationScheme);
    const isValid = await validator.validate({ UserImageFiles });

    if (!isValid) {
      setErrors(validator.errors);
      return;
    }

    const fileExtension = UserImageFiles[0].FileExtension.toLowerCase();

    setUserImage({
      file: UserImageFiles[0].FileData,
      fileName: UserImageFiles[0].FileName,
      mimeType:
        fileExtension === '.gif'
          ? 'image/gif'
          : fileExtension === '.jpg' || fileExtension === '.jpeg'
          ? 'image/jpeg'
          : fileExtension === '.png'
          ? 'image/png'
          : 'application/unknown',
    });

    onHideUploadDialog();
  };

  const findLabelId = (labelTypes, pattern) => {
    const match = labelTypes.find((labelType) => pattern.test(labelType.NomTipoDBCContactLabel || ''));

    if (match) {
      return match.ClaTipoDBCContactLabel;
    }

    return labelTypes.length > 0 ? labelTypes[0].ClaTipoDBCContactLabel : null;
  };

  const saveContactRow = (contactId, labelId, valor, onSaved) => {
    if (!valor) {
      showSweetAlert(null, t('common:RequiredField'), 'warning');
      return;
    }

    if (contactId === NEW_OPTION) {
      const url = `${config.UrlApiProject}QRCode/InsertMyContactInfoRow`;
      const paramsService = {
        ClaTipoDBCContactLabel: labelId,
        Valor: valor,
      };
      callApi(url, 'POST', paramsService, () => {
        showSweetAlert(null, t('common:Success'), 'success');
        onSaved();
      });
    } else {
      const row = [...Emails, ...Phones].find((item) => item.ContactId === contactId);
      const url = `${config.UrlApiProject}QRCode/UpdateMyContactInfoRow`;
      const paramsService = {
        ContactId: contactId,
        ClaTipoDBCContactLabel: row ? row.ClaTipoDBCContactLabel : labelId,
        Valor: valor,
      };
      callApi(url, 'POST', paramsService, () => {
        showSweetAlert(null, t('common:Success'), 'success');
        onSaved();
      });
    }
  };

  const onSaveNewEmailClick = () => {
    saveContactRow(NEW_OPTION, findLabelId(EmailLabelTypes, /work|corporate/i), NewEmailValue.trim(), () => {
      setNewEmailValue('');
      setFeaturedEmailId(NONE_OPTION);
      getMyBusinessCard(true);
    });
  };

  const onDeletePhoneClick = (contactId) => {
    const url = `${config.UrlApiProject}QRCode/DeleteMyContactInfoRow`;
    callApi(url, 'POST', { ContactId: contactId }, () => {
      showSweetAlert(null, t('common:Success'), 'success');
      setMobileContactId(NONE_OPTION);
      setOfficeContactId(NONE_OPTION);
      getMyBusinessCard(true);
    });
  };

  const onSaveNewMobileClick = () => {
    if (!NewMobileValue || !isValidPhoneNumber(NewMobileValue)) {
      showSweetAlert(null, t('common:10DigitPhoneError'), 'warning');
      return;
    }

    saveContactRow(NEW_OPTION, findLabelId(PhoneLabelTypes, /mobile|cell/i), NewMobileValue, () => {
      setNewMobileValue('');
      setMobileContactId(NONE_OPTION);
      getMyBusinessCard(true);
    });
  };

  const onSaveNewOfficeClick = () => {
    if (!NewOfficeValue || !isValidPhoneNumber(NewOfficeValue)) {
      showSweetAlert(null, t('common:10DigitPhoneError'), 'warning');
      return;
    }

    saveContactRow(NEW_OPTION, findLabelId(PhoneLabelTypes, /work|office|direct/i), NewOfficeValue, () => {
      setNewOfficeValue('');
      setOfficeContactId(NONE_OPTION);
      getMyBusinessCard(true);
    });
  };

  const emailOptions = [
    { label: t('qr:NoneOption'), value: NONE_OPTION },
    ...Emails.map((row) => ({ label: `${row.Valor} (${row.NomTipoDBCContactLabel})`, value: row.ContactId })),
    { label: t('qr:AddNewOption'), value: NEW_OPTION },
  ];

  const phoneOptionRow = (row) => ({
    label: `${formatPhoneForSignature(row.Valor)} (${row.NomTipoDBCContactLabel})`,
    value: row.ContactId,
  });

  // A saved featured number whose label no longer matches the slot's type filter must
  // still appear as the selected option — otherwise the dropdown renders empty while
  // the number keeps showing on the signature
  const withSelectedRow = (options, selectedId) => {
    if (typeof selectedId === 'number' && selectedId > 0 && !options.some((option) => option.value === selectedId)) {
      const row = Phones.find((item) => item.ContactId === selectedId);
      if (row) {
        options.splice(options.length - 1, 0, phoneOptionRow(row));
      }
    }
    return options;
  };

  // Each slot only offers numbers of its own type; office gets everything non-mobile
  // (Work/Office/Direct/etc.) so no saved number becomes unselectable
  const mobilePhoneOptions = withSelectedRow(
    [
      { label: t('qr:NoneOption'), value: NONE_OPTION },
      ...Phones.filter(isMobilePhoneRow).map(phoneOptionRow),
      { label: t('qr:AddNewOption'), value: NEW_OPTION },
    ],
    MobileContactId
  );

  const officePhoneOptions = withSelectedRow(
    [
      { label: t('qr:NoneOption'), value: NONE_OPTION },
      ...Phones.filter((row) => !isMobilePhoneRow(row)).map(phoneOptionRow),
      { label: t('qr:AddNewOption'), value: NEW_OPTION },
    ],
    OfficeContactId
  );

  const renderUserImage = () => {
    const src = UserImage.file ? `data:${UserImage.mimeType};base64,${UserImage.file}` : defaultAvatar;
    return (
      <div className="email-signature-photo-wrapper">
        <img src={src} alt="" className="email-signature-photo" />
        {UserImage.file && (
          <FieldButton
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-sm email-signature-photo-remove"
            onClick={onRemovePhotoClick}
            tooltip={t('qr:RemovePhoto')}
          />
        )}
      </div>
    );
  };

  if (IsLoading) {
    return (
      <PageContent title={t('qr:MyBusinessCard')}>
        <Skeleton height="14rem" className="mb-3" />
        <Skeleton height="20rem" />
      </PageContent>
    );
  }

  if (!HasCard) {
    // Only reachable when the automatic first-visit creation failed
    return (
      <PageContent title={t('qr:MyBusinessCard')}>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="text-center">
                <h3>{t('qr:SignatureSetupFailed')}</h3>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </PageContent>
    );
  }

  return (
    <PageContent title={t('qr:MyBusinessCard')}>
      <Row className="align-items-start">
        {/* order classes place the edit panel left and the preview right on desktop */}
        <Col xs={12} lg={7} className="order-2 order-lg-2">
          <Card>
            <CardBody>
              <TabView>
                <TabPanel header={t('qr:EmailSignature')}>
                  <div className="email-signature-preview-frame">
                    <iframe
                      title={t('qr:SignaturePreview')}
                      srcDoc={buildPreviewSrcDoc(signatureData(BannerVersion))}
                      className="email-signature-preview-iframe"
                      scrolling="no"
                    />
                  </div>
                  <div className="mt-3 text-muted text-sm">
                    <i className="pi pi-info-circle mr-2 text-primary" />
                    {t('qr:OutlookInstructionsBody')}
                  </div>
                  <div className="mt-3">
                    <FieldButton
                      label={t('qr:CopySignature')}
                      icon="pi pi-copy"
                      className="p-button-success"
                      onClick={onCopySignatureClick}
                    />
                  </div>
                </TabPanel>
                <TabPanel header={t('qr:BusinessCard')}>
                  <div className="mb-3">
                    <FieldButton
                      label={t('qr:AddToWallet')}
                      icon="pi pi-qrcode"
                      className="p-button-success"
                      onClick={onShowWalletQRClick}
                    />
                  </div>
                  <div className="email-signature-preview-frame">
                    {/* key remounts the card after each save so it refetches fresh data */}
                    {ClaUsuarioBC && <BusinessCard key={BannerVersion} ClaUsuarioBC={ClaUsuarioBC} hideActions className="border border-light rounded" />}
                  </div>
                </TabPanel>
              </TabView>
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} lg={5} className="order-1 order-lg-1">
          <Card>
            <CardHeader>
              <h5 className="h3 mb-0 text-white">{t('qr:EditMyInfo')}</h5>
            </CardHeader>
            <CardBody className="p-fluid">
              <Row>
                <Col xs={12} className="text-center p-field">
                  {renderUserImage()}
                  <div className="mt-2">
                    <FieldButton
                      label={t('qr:UploadPhoto')}
                      icon="pi pi-image"
                      className="p-button-outlined p-button-sm"
                      onClick={() => setShowUploadDialog(true)}
                    />
                  </div>
                </Col>
                <Col xs={12} className="p-field">
                  <Label>{t('qr:FullName')}</Label>
                  <FieldText
                    name="FullName"
                    value={FullName}
                    onChange={(e) => setFullName(e.target.value)}
                    errors={errors}
                  />
                </Col>
                <Col xs={12} className="p-field">
                  <Label>{t('qr:JobTitle')}</Label>
                  <FieldText
                    name="JobTitle"
                    value={JobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    errors={errors}
                  />
                </Col>
                <Col xs={12} className="p-field">
                  <Label>{t('qr:Address')}</Label>
                  <FieldText
                    name="FullAddress"
                    value={FullAddress}
                    onChange={(e) => setFullAddress(e.target.value)}
                    errors={errors}
                  />
                </Col>
                <Col xs={12} className="p-field">
                  <FieldButton
                    label={t('common:Save')}
                    icon="pi pi-save"
                    className="p-button-success"
                    onClick={onSaveInfoClick}
                  />
                </Col>
                <Col xs={12} className="p-field">
                  <Label>{t('qr:FeaturedEmail')}</Label>
                  <FieldDropdown
                    name="FeaturedEmailId"
                    value={FeaturedEmailId}
                    options={emailOptions}
                    optionLabel="label"
                    optionValue="value"
                    showClear={false}
                    onChange={(e) => {
                      const value = e.value === null || e.value === undefined ? NONE_OPTION : e.value;
                      setFeaturedEmailId(value);
                      if (value !== NEW_OPTION) {
                        persistSelections(value, MobileContactId, OfficeContactId);
                      }
                    }}
                    errors={errors}
                  />
                </Col>
                {FeaturedEmailId === NEW_OPTION && (
                  <Col xs={12} className="p-field">
                    <div className="p-inputgroup">
                      <FieldText
                        name="NewEmailValue"
                        value={NewEmailValue}
                        placeholder={t('qr:EmailAddress')}
                        onChange={(e) => setNewEmailValue(e.target.value)}
                        errors={errors}
                      />
                      <FieldButton icon="pi pi-check" className="p-button-primary" onClick={onSaveNewEmailClick} />
                    </div>
                  </Col>
                )}
                <Col xs={12} className="p-field">
                  <Label>{t('qr:MobilePhone')}</Label>
                  <div className="p-inputgroup">
                    <FieldDropdown
                      name="MobileContactId"
                      value={MobileContactId}
                      options={mobilePhoneOptions}
                      optionLabel="label"
                      optionValue="value"
                      showClear={false}
                      onChange={(e) => {
                        const value = e.value === null || e.value === undefined ? NONE_OPTION : e.value;
                        setMobileContactId(value);
                        if (value !== NEW_OPTION) {
                          persistSelections(FeaturedEmailId, value, OfficeContactId);
                        }
                      }}
                      errors={errors}
                    />
                    {typeof MobileContactId === 'number' && MobileContactId > 0 && (
                      <FieldButton
                        icon="pi pi-trash"
                        className="p-button-danger"
                        tooltip={t('qr:DeletePhoneNumber')}
                        onClick={() => onDeletePhoneClick(MobileContactId)}
                      />
                    )}
                  </div>
                </Col>
                {MobileContactId === NEW_OPTION && (
                  <Col xs={12} className="p-field">
                    <div className="p-inputgroup">
                      <FieldPhone
                        name="NewMobileValue"
                        international={false}
                        value={NewMobileValue}
                        placeholder={t('common:ContactNumber')}
                        onChange={(phone) => setNewMobileValue(phone)}
                        onCountryChange={() => {}}
                        errors={errors}
                      />
                      <FieldButton icon="pi pi-check" className="p-button-primary" onClick={onSaveNewMobileClick} />
                    </div>
                  </Col>
                )}
                <Col xs={12} className="p-field">
                  <Label>{t('qr:OfficePhone')}</Label>
                  <div className="p-inputgroup">
                    <FieldDropdown
                      name="OfficeContactId"
                      value={OfficeContactId}
                      options={officePhoneOptions}
                      optionLabel="label"
                      optionValue="value"
                      showClear={false}
                      onChange={(e) => {
                        const value = e.value === null || e.value === undefined ? NONE_OPTION : e.value;
                        setOfficeContactId(value);
                        if (value !== NEW_OPTION) {
                          persistSelections(FeaturedEmailId, MobileContactId, value);
                        }
                      }}
                      errors={errors}
                    />
                    {typeof OfficeContactId === 'number' && OfficeContactId > 0 && (
                      <FieldButton
                        icon="pi pi-trash"
                        className="p-button-danger"
                        tooltip={t('qr:DeletePhoneNumber')}
                        onClick={() => onDeletePhoneClick(OfficeContactId)}
                      />
                    )}
                  </div>
                </Col>
                {OfficeContactId === NEW_OPTION && (
                  <Col xs={12} className="p-field">
                    <div className="p-inputgroup">
                      <FieldPhone
                        name="NewOfficeValue"
                        international={false}
                        value={NewOfficeValue}
                        placeholder={t('common:ContactNumber')}
                        onChange={(phone) => setNewOfficeValue(phone)}
                        onCountryChange={() => {}}
                        errors={errors}
                      />
                      <FieldButton icon="pi pi-check" className="p-button-primary" onClick={onSaveNewOfficeClick} />
                    </div>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Dialog
        visible={ShowWalletQRDialog}
        onHide={() => setShowWalletQRDialog(false)}
        header={t('qr:AddToWallet')}
      >
        <Row>
          <Col xs={12} className="text-center">
            <img
              src={`data:${WalletQR.mimeType};base64,${WalletQR.file}`}
              alt={t('qr:AddToWallet')}
              style={{ maxWidth: '60vw', maxHeight: '60vh' }}
            />
            <div className="text-muted text-sm mt-2">{t('qr:ScanToAddWallet')}</div>
          </Col>
        </Row>
      </Dialog>
      <Dialog
        visible={ShowUploadDialog}
        onHide={onHideUploadDialog}
        style={{ minWidth: '10vw', minHeight: '20vh' }}
        header={t('qr:UploadPhoto')}
      >
        <Row>
          <Col xs={12} className="mb-2">
            <div className="text-info">
              <i className="pi pi-info-circle" /> {t('qr:AcceptedImageTypesProfilePic')}
            </div>
          </Col>
          <Col xs={12}>
            <FieldUpload
              name="UserImageFiles"
              files={UserImageFiles}
              onChange={onUserImageFilesChange}
              errors={errors}
              accept="image/jpeg,image/png,image/gif"
              uploadPrompt={t('qr:DragAndDropPhotoHere')}
            />
          </Col>
          <Col xs={12} className="p-fluid mt-2">
            <FieldButton label={t('common:Submit')} icon="pi pi-save" onClick={onSubmitUserImageClick} />
          </Col>
        </Row>
      </Dialog>
    </PageContent>
  );
};

export default MyBusinessCardScreen;
